/**
 * environmentAnchorStore — the ACTIVE environment's named anchors, and their
 * save state (ENG-590).
 *
 * A module singleton rather than React state, for the reason bakeStatusStore
 * and walkHereState are: TWO surfaces author the same anchors and neither owns
 * the other — the viewport right-click ("Place anchor here", a Canvas gesture
 * with no inspector mounted) and the EnvironmentInspector's ANCHORS section (a
 * DOM panel that may be open on a DIFFERENT environment than the live one).
 * Give each its own copy and the second one to save silently discards the
 * first's work, which is the stale-draft failure the UX rules name.
 *
 * ── What it is scoped to ────────────────────────────────────────────────────
 * ONE environment, named by its SERVER asset id. Every mutator takes that id
 * and REFUSES when it does not match the loaded one, rather than applying the
 * edit to whatever happens to be loaded: a right-click lands on the live
 * environment while the inspector can be showing another, and an anchor written
 * onto the wrong row is populated, plausible and past every empty check.
 *
 * ── Save is explicit ────────────────────────────────────────────────────────
 * Edits go `clean -> dirty` and stay there until someone saves. The exception
 * is the viewport gesture, which saves immediately: a right-click has no Save
 * button in front of it, and a placement that silently sat unsaved until the
 * user happened to open the inspector would be lost on reload with no signal.
 * That is a call the CALLER makes (place() then save()), not a mode here.
 *
 * The server is the source of truth: `saved` is the last map the server
 * acknowledged, and `state` is derived against it, so a failed PATCH leaves the
 * edit visible and dirty rather than pretending it landed.
 */

import { anchorsEqual, readAnchors, serializeAnchors, withAnchor, withLabel, withoutAnchor, withRenamedAnchor } from './environmentAnchors.js'
import { environmentAssetId } from './environmentAssetId.js'

// The asset manager reaches this module through an extensionless specifier only
// Vite resolves, so it is imported lazily — the same recipe environmentFiles.js
// uses, and what keeps this file loadable under `node --test`.
let managerOverride = null
async function manager() {
  return managerOverride ?? (await import('./CustomAssetManager.js')).CustomAssetManager
}

/** Test seam: swap the asset manager for a fake. */
export function _setManagerForTests(m) { managerOverride = m ?? null }

let state = {
  assetId: null,        // server id of the environment these anchors belong to
  assetName: null,      // for messages — an id is not something to show a user
  anchors: {},          // the working copy, including unsaved edits
  saved: {},            // the last map the server acknowledged
  dropped: [],          // names the row carried that could not be read
  state: 'clean',       // clean | dirty | saving | error   (DirtyBadge's vocabulary)
  error: null,
}

const listeners = new Set()
function emit() { for (const fn of listeners) fn() }

function set(patch) {
  state = { ...state, ...patch }
  emit()
}

/** Derive clean/dirty from the working copy against what the server has. */
function settled(anchors, saved) {
  return anchorsEqual(anchors, saved) ? 'clean' : 'dirty'
}

export const environmentAnchorStore = {
  getState() { return state },
  subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn) },

  /**
   * Adopt an environment row as the subject.
   *
   * A reload of the SAME environment keeps unsaved edits: the row is re-read on
   * every content-browser refresh and on every PATCH of an unrelated field, and
   * dropping a half-typed anchor list on each of those would make the panel
   * unusable. A DIFFERENT environment replaces everything — carrying one
   * environment's anchors onto another is exactly the write this store exists
   * to prevent.
   */
  load(asset) {
    // environmentAssetId is the documented owner of the registry's
    // `environment:<uuid>` unwrapping: a namespaced id reaching the PATCH is a
    // 400 the server calls "invalid asset id" (see environmentAssetId.js).
    const assetId = environmentAssetId(asset)
    const { anchors, dropped } = readAnchors(asset)
    if (assetId && assetId === state.assetId) {
      set({ saved: anchors, dropped, state: settled(state.anchors, anchors) })
      return
    }
    set({
      assetId,
      assetName: asset?.name ?? null,
      anchors,
      saved: anchors,
      dropped,
      state: 'clean',
      error: null,
    })
  },

  /** Forget the subject (no environment selected). */
  clear() {
    set({ assetId: null, assetName: null, anchors: {}, saved: {}, dropped: [], state: 'clean', error: null })
  },

  /**
   * Place (or move) `name` on environment `assetId`. `anchor` is
   * `{px, py, pz, label?}` -- the shape the server stores.
   *
   * Returns true when it applied. A mismatched id returns false and records the
   * reason rather than throwing: the caller is a menu click, and the answer the
   * user needs is "that is not the environment you are looking at", not a stack
   * trace.
   */
  place(assetId, name, anchor) {
    if (!this._owns(assetId)) return false
    try {
      const anchors = withAnchor(state.anchors, name, anchor)
      set({ anchors, state: settled(anchors, state.saved), error: null })
      return true
    } catch (err) {
      set({ error: String(err?.message ?? err), state: 'error' })
      return false
    }
  },

  /** Set (or clear) an anchor's LABEL -- the prose the character LLM reads. */
  setLabel(assetId, name, label) {
    if (!this._owns(assetId)) return false
    try {
      const anchors = withLabel(state.anchors, name, label)
      set({ anchors, state: settled(anchors, state.saved), error: null })
      return true
    } catch (err) {
      set({ error: String(err?.message ?? err), state: 'error' })
      return false
    }
  },

  remove(assetId, name) {
    if (!this._owns(assetId)) return false
    const anchors = withoutAnchor(state.anchors, name)
    set({ anchors, state: settled(anchors, state.saved), error: null })
    return true
  },

  rename(assetId, from, to) {
    if (!this._owns(assetId)) return false
    try {
      const anchors = withRenamedAnchor(state.anchors, from, to)
      set({ anchors, state: settled(anchors, state.saved), error: null })
      return true
    } catch (err) {
      set({ error: String(err?.message ?? err), state: 'error' })
      return false
    }
  },

  /** Throw away unsaved edits and go back to what the server has. */
  revert() {
    set({ anchors: state.saved, state: 'clean', error: null })
  },

  /**
   * PATCH the working copy. Resolves true on success.
   *
   * `saved` moves only on a server acknowledgement, so a failure leaves the
   * panel dirty with the edit intact — never a green badge over a write that
   * did not happen.
   */
  async save(assetId) {
    if (!this._owns(assetId)) return false
    if (state.state === 'saving') return false
    let body
    try {
      body = serializeAnchors(state.anchors)
    } catch (err) {
      set({ error: String(err?.message ?? err), state: 'error' })
      return false
    }
    const sending = state.anchors
    set({ state: 'saving', error: null })
    try {
      const m = await manager()
      await m.updateEnvironmentAsset(assetId, { anchors: body })
      // Another edit may have landed while the request was in flight; settle
      // against what is on screen NOW, not against what was sent.
      set({ saved: sending, state: settled(state.anchors, sending) })
      return true
    } catch (err) {
      set({ error: String(err?.message ?? err), state: 'error' })
      return false
    }
  },

  /** Internal: is `assetId` the subject? Records the refusal when it is not. */
  _owns(assetId) {
    if (assetId && assetId === state.assetId) return true
    set({
      error: state.assetId
        ? `anchor edit addressed environment ${assetId}, but ${state.assetName ?? state.assetId} is loaded`
        : 'no environment is loaded',
      state: 'error',
    })
    return false
  },
}

if (typeof window !== 'undefined') window.__ENV_ANCHORS__ = environmentAnchorStore
